import React, { useCallback, useEffect, useRef, useState } from "react";

type VoiceCapturePanelApi = {
  closePanel: () => void;
};

type VoiceCaptureIconMenuProps = {
  active?: boolean;
  onToggle?: () => void;
  onLanguageHoverStart?: () => void;
  onLanguageHoverEnd?: () => void;
  onLanguageClick?: () => void;
  showLanguagePanel?: boolean;
  languagePanel?: React.ReactNode | ((api: VoiceCapturePanelApi) => React.ReactNode);
  defaultLanguagePanelOpen?: boolean;
  onLanguagePanelOpenChange?: (nextOpen: boolean) => void;
  panelClassName?: string;
  className?: string;
};

const VoiceCaptureIconMenu = ({
  active = false,
  onToggle,
  onLanguageHoverStart,
  onLanguageHoverEnd,
  onLanguageClick,
  showLanguagePanel,
  languagePanel,
  defaultLanguagePanelOpen = false,
  onLanguagePanelOpenChange,
  panelClassName = "",
  className = "",
}: VoiceCaptureIconMenuProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const isControlled = typeof showLanguagePanel === "boolean";
  const [internalOpen, setInternalOpen] = useState(defaultLanguagePanelOpen);
  const panelOpen = isControlled ? Boolean(showLanguagePanel) : internalOpen;

  const setPanelOpen = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      onLanguagePanelOpenChange?.(nextOpen);
    },
    [isControlled, onLanguagePanelOpenChange]
  );

  const closePanel = useCallback(() => {
    setPanelOpen(false);
  }, [setPanelOpen]);

  const openPanel = useCallback(() => {
    setPanelOpen(true);
  }, [setPanelOpen]);

  useEffect(() => {
    if (!panelOpen) return;
    const handleOutsidePress = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      closePanel();
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePanel();
      }
    };
    document.addEventListener("mousedown", handleOutsidePress);
    document.addEventListener("touchstart", handleOutsidePress);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsidePress);
      document.removeEventListener("touchstart", handleOutsidePress);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [closePanel, panelOpen]);

  const handleLanguageHoverStart = () => {
    openPanel();
    onLanguageHoverStart?.();
  };

  const handleLanguageHoverEnd = () => {
    onLanguageHoverEnd?.();
  };

  const handleLanguageClick = () => {
    if (!isControlled) {
      setInternalOpen((prev) => !prev);
    }
    onLanguagePanelOpenChange?.(!panelOpen);
    onLanguageClick?.();
  };

  const renderedPanel =
    typeof languagePanel === "function"
      ? languagePanel({ closePanel })
      : languagePanel;

  return (
    <div
      ref={rootRef}
      className={"relative inline-flex " + className}
      onMouseLeave={handleLanguageHoverEnd}
    >
      <div className="inline-flex items-center overflow-hidden rounded-md border border-default-300 bg-content2">
        <button
          type="button"
          aria-label={active ? "Stop voice capture" : "Start voice capture"}
          className={
            "inline-flex h-8 w-8 items-center justify-center " +
            (active
              ? "text-success bg-success/10"
              : "text-default-500 hover:bg-default-100")
          }
          onClick={onToggle}
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
            <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Z" />
            <path d="M19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.93V20H9a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-2.07A7 7 0 0 0 19 11Z" />
          </svg>
        </button>
        <span aria-hidden="true" className="h-5 w-px bg-default-300" />
        <button
          type="button"
          aria-label="Language options"
          className="inline-flex h-8 w-8 items-center justify-center text-default-500 hover:bg-default-100"
          onMouseEnter={handleLanguageHoverStart}
          onFocus={handleLanguageHoverStart}
          onBlur={handleLanguageHoverEnd}
          onClick={handleLanguageClick}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
            <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm6.93 9h-2.12a15.6 15.6 0 0 0-1.02-4.05A8.03 8.03 0 0 1 18.93 11ZM12 4c.9 1.2 1.6 2.7 2.02 4.5H9.98C10.4 6.7 11.1 5.2 12 4Zm-6.93 7h2.12c.2-1.46.56-2.84 1.02-4.05A8.03 8.03 0 0 0 5.07 11Zm0 2a8.03 8.03 0 0 0 3.14 4.05c-.46-1.21-.82-2.59-1.02-4.05H5.07Zm3.84 0h6.18c-.42 1.8-1.12 3.3-2.02 4.5-.9-1.2-1.6-2.7-2.02-4.5Zm7.9 0h2.12a8.03 8.03 0 0 1-3.14 4.05c.46-1.21.82-2.59 1.02-4.05ZM16.81 11h2.12a8.03 8.03 0 0 0-3.14-4.05c.46 1.21.82 2.59 1.02 4.05ZM7.19 11h2.12c.2-1.46.56-2.84 1.02-4.05A8.03 8.03 0 0 0 5.07 11ZM9.98 12h4.04c-.2 1.46-.56 2.84-1.02 4.05A8.03 8.03 0 0 1 9.98 12Z" />
          </svg>
        </button>
      </div>
      {renderedPanel ? (
        <div
          className={
            "absolute right-0 top-0 mt-9 z-20 " +
            (panelOpen ? "block " : "hidden ") +
            panelClassName
          }
        >
          {renderedPanel}
        </div>
      ) : null}
    </div>
  );
};

export default VoiceCaptureIconMenu;
export { VoiceCaptureIconMenu };
